import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteLayout from "@/components/SiteLayout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { IMAGES, SITE, buildCityServiceContent } from "@/data/siteContent";
import { usePageMeta } from "@/hooks/usePageMeta";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
  viewport: { once: true },
};

const CityServicePage = ({ language = "fr", city, serviceKey }) => {
  const content = buildCityServiceContent(language, city, serviceKey);
  const metaTitle = content.metaTitle || content.h1;
  const metaDescription =
    content.metaDescription ||
    "Service local de pavé uni sur la Rive-Sud.";

  usePageMeta(metaTitle, metaDescription);
  const isFr = language === "fr";

  const renderParagraph = (text, testId) => (
    <p className="text-muted-foreground" data-testid={testId}>
      {text}
    </p>
  );

  return (
    <SiteLayout language={language}>
      <section className="bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 space-y-6">
          <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="city-hero-eyebrow">
            {isFr ? "Page locale" : "Local page"}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold text-primary" data-testid={`city-${city.slug}-${serviceKey}-h1`}>
            {content.h1}
          </h1>
          {city.intro ? (
            <p className="text-lg text-muted-foreground">{city.intro}</p>
          ) : (
            renderParagraph(content.intro, `city-${city.slug}-${serviceKey}-intro`)
          )}
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 space-y-12">
          <motion.div {...fadeUp} className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" data-testid={`city-${city.slug}-${serviceKey}-problems-title`}>
              {isFr ? "Problèmes courants" : "Common problems"}
            </h2>
            {city.problemLocal ? (
              <p className="text-muted-foreground">{city.problemLocal}</p>
            ) : (
              renderParagraph(content.problems, `city-${city.slug}-${serviceKey}-problems-text`)
            )}
          </motion.div>

          <motion.div {...fadeUp} className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" data-testid={`city-${city.slug}-${serviceKey}-method-title`}>
              {isFr ? "Méthode de travail professionnelle" : "Professional method"}
            </h2>
            {renderParagraph(content.method, `city-${city.slug}-${serviceKey}-method-text`)}
          </motion.div>

          <motion.div
            {...fadeUp}
            className="relative border border-border bg-white p-8 shadow-sm textured-panel"
            style={{ "--texture-url": `url(${IMAGES.textureStone}&w=1000&q=50)` }}
            data-testid={`city-${city.slug}-${serviceKey}-why-section`}
          >
            <h2 className="text-2xl font-bold text-primary" data-testid={`city-${city.slug}-${serviceKey}-why-title`}>
              {isFr ? "Pourquoi choisir notre entreprise" : "Why choose our company"}
            </h2>
            <div className="mt-4">
              {renderParagraph(content.why, `city-${city.slug}-${serviceKey}-why-text`)}
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" data-testid={`city-${city.slug}-${serviceKey}-pricing-title`}>
              {isFr ? "Prix et estimation" : "Pricing and estimates"}
            </h2>
            {renderParagraph(content.pricing, `city-${city.slug}-${serviceKey}-pricing-text`)}
          </motion.div>

          <motion.div {...fadeUp} className="space-y-4">
            <h2 className="text-2xl font-bold text-primary" data-testid={`city-${city.slug}-${serviceKey}-areas-title`}>
              {isFr ? "Secteurs desservis" : "Areas served"}
            </h2>
            {renderParagraph(content.areas, `city-${city.slug}-${serviceKey}-areas-text`)}
            <ul className="grid gap-2 md:grid-cols-2" data-testid={`city-${city.slug}-${serviceKey}-areas-list`}>
              {(city.secteurs || content.areasList).map((area, index) => (
                <li
                  key={area}
                  className="border border-border bg-slate-50 px-4 py-2 text-sm text-foreground"
                  data-testid={`city-${city.slug}-${serviceKey}-areas-item-${index + 1}`}
                >
                  {area}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold" data-testid={`city-${city.slug}-${serviceKey}-faq-title`}>
              {isFr ? "FAQ locale" : "Local FAQ"}
            </h2>
            <Accordion type="single" collapsible className="mt-8" data-testid={`city-${city.slug}-${serviceKey}-faq-accordion`}>
              {city.faqLocal ? (
                <AccordionItem value="faq-local-1">
                  <AccordionTrigger>{isFr ? "Question fréquente à " : "Common question in "}{city.name}</AccordionTrigger>
                  <AccordionContent>{city.faqLocal}</AccordionContent>
                </AccordionItem>
              ) : null}
              {content.faqs.map((item, index) => (
                <AccordionItem
                  key={item.question}
                  value={`faq-${index}`}
                  data-testid={`city-${city.slug}-${serviceKey}-faq-item-${index + 1}`}
                >
                  <AccordionTrigger data-testid={`city-${city.slug}-${serviceKey}-faq-trigger-${index + 1}`}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent data-testid={`city-${city.slug}-${serviceKey}-faq-content-${index + 1}`}>
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeUp} className="border border-border bg-white p-10 shadow-sm space-y-6">
            <h2 className="text-3xl font-bold text-primary" data-testid={`city-${city.slug}-${serviceKey}-cta-title`}>
              {content.ctaTitle}
            </h2>
            <p className="text-muted-foreground" data-testid={`city-${city.slug}-${serviceKey}-cta-text`}>
              {content.ctaText}
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Button
                asChild
                className="rounded-none uppercase tracking-wider font-bold"
                data-testid={`city-${city.slug}-${serviceKey}-cta-button`}
              >
                <Link to={isFr ? "/fr/contact" : "/en/contact"}>{content.ctaButton}</Link>
              </Button>
              <a
                href={`tel:${SITE.phone}`}
                className="text-sm font-semibold text-primary"
                data-testid={`city-${city.slug}-${serviceKey}-cta-phone`}
              >
                {isFr ? `Appeler: ${SITE.phoneDisplay}` : `Call: ${SITE.phoneDisplay}`}
              </a>
              <Link
                to={content.relatedService.path}
                className="text-sm uppercase tracking-widest text-secondary"
                data-testid={`city-${city.slug}-${serviceKey}-related-link`}
              >
                {isFr
                  ? `Voir aussi : ${content.relatedService.label}`
                  : `Also explore: ${content.relatedService.label}`}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <iframe
            title="Formulaire de soumission"
            src="https://spaceleadx.ai/soumission/estimation-pave-uni"
            width="100%"
            height="1100"
            frameBorder="0"
            style={{
              border: "none",
              maxWidth: "600px",
              margin: "0 auto",
              display: "block",
            }}
            data-testid="city-spaceleadx-iframe"
          />
        </div>
      </section>
    </SiteLayout>
  );
};

export default CityServicePage;